/**
 * Date utilities: formatting, ranges, and regex patterns.
 */
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Idempotent — `init()` in core/main.tsx also extends these for the app.
// Loading them here lets the tz-aware date helpers work in unit tests
// (which don't run `init()`) and in any module that imports them before app boot.
dayjs.extend(utc);
dayjs.extend(timezone);

/** Milliseconds in one minute. */
export const MS_PER_MINUTE = 60_000;

/** Milliseconds in one hour. */
export const MS_PER_HOUR = 3_600_000;

/** Milliseconds in one calendar day — the canonical day-in-ms constant. */
export const MS_PER_DAY = 86_400_000;

/** Milliseconds in one second. */
export const MS_PER_SECOND = 1_000;

/** Milliseconds in one week. */
export const MS_PER_WEEK = 604_800_000;

/**
 * Fixed-duration unit letter → milliseconds. Only constant-length spans live
 * here; calendar-variable units (month/quarter/year) are intentionally
 * excluded — use `time-bucket`'s `Period` model for those. `m` is minutes,
 * consistent with `INTERVAL_TO_PERIOD`'s `"1m"` → minute.
 */
const DURATION_UNIT_MS = {
  ms: 1,
  s: MS_PER_SECOND,
  m: MS_PER_MINUTE,
  h: MS_PER_HOUR,
  d: MS_PER_DAY,
  w: MS_PER_WEEK,
} as const;

/** Spelled-out time-unit token → milliseconds, for `:per <unit>` rate axes. */
export const TIME_UNIT_MS = {
  second: MS_PER_SECOND,
  minute: MS_PER_MINUTE,
  hour: MS_PER_HOUR,
  day: MS_PER_DAY,
  week: MS_PER_WEEK,
} as const;

/** A spelled-out fixed-length time-unit token. */
export type TimeUnit = keyof typeof TIME_UNIT_MS;

const DURATION_RE = /^(\d+(?:\.\d+)?)(ms|s|m|h|d|w)$/;

/**
 * Parse a fixed-duration string (`"7d"`, `"30m"`, `"12h"`, `"1w"`, `"500ms"`)
 * to milliseconds, or `null` on malformed input. Units are constant-length
 * only (ms/s/m/h/d/w); `m` is minutes.
 */
export const parseDurationMs = (text: string): number | null => {
  const match = DURATION_RE.exec(text.trim());
  if (match === null) {
    return null;
  }
  const [, value, unit] = match;
  return Number(value) * DURATION_UNIT_MS[unit as keyof typeof DURATION_UNIT_MS];
};

/** Resolve a `:per` rate-axis token (`"day"`, `"hour"`, …) to milliseconds, or `null`. */
export const parseTimeUnitMs = (unit: string): number | null => TIME_UNIT_MS[unit.trim() as TimeUnit] ?? null;

export function* getDateRange(start: dayjs.Dayjs, end: dayjs.Dayjs, unit: dayjs.ManipulateType) {
  let current = start.clone().startOf(unit).subtract(1, unit);
  const last = end.subtract(1, unit);
  while (current < last) {
    current = current.add(1, unit);
    yield current;
  }
  return false;
}

/**
 * Strict ISO 8601 date pattern.
 * Matches: YYYY-MM-DD with optional time component.
 * Examples: "2024-01-15", "2024-01-15T10:30:00", "2024-01-15T10:30:00.123Z", "2024-01-15T10:30:00+02:00"
 */
export const ISO_DATE_REGEX_STRICT = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/;

/**
 * Basic ISO 8601 date pattern.
 * Matches strings that start with YYYY-MM-DD format.
 * Used for quick type detection where full validation isn't needed.
 */
export const ISO_DATE_REGEX_BASIC = /^\d{4}-\d{2}-\d{2}/;

/**
 * Canonical `dayjs` format pattern for a calendar date (no time component).
 * SSOT for the `'YYYY-MM-DD'` date-only format used across formatters and
 * value coercion — wider patterns (e.g. `'YYYY-MM-DD HH:mm'`) are distinct
 * and intentionally not unified here.
 */
export const DATE_FORMAT_PATTERN = 'YYYY-MM-DD';
/**
 * Soft-parse a value as a date — returns a {@link dayjs.Dayjs} on success,
 * `null` on failure. Accepts strings, numbers (Unix milliseconds), and
 * `Date` objects.
 *
 * Use when the caller has a sensible fallback for invalid input — filter
 * predicates that should silently reject malformed cells, or normalisers
 * that pass through unparseable values (e.g. CDF's relative-time strings
 * like `"30d-ago"` are intentionally `null` here so callers can leave them
 * untouched for server-side resolution).
 */
export function tryParseDate(value: unknown): dayjs.Dayjs | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date || typeof value === 'string' || typeof value === 'number') {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }
  return null;
}

/**
 * Strict-parse a value as a date — throws on invalid input. Use when the
 * caller's contract guarantees a valid date and silently substituting
 * `null` would corrupt downstream computation (e.g. user-supplied date
 * arguments to derive expressions).
 */
export function parseDate(value: unknown): dayjs.Dayjs {
  const d = tryParseDate(value);
  if (d === null) {
    throw new Error('Invalid date format. Use ISO 8601 (YYYY-MM-DD)');
  }
  return d;
}
